import type { TFileWithMeta } from '@components/Files/UploadProvider/types/TFileWithMeta';
import { useAPI } from '@providers/useAPI';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import type { CommonBullJobResponseDto } from 'app/API';
import { createFetchMediaAnalysisStatusQueryFn } from 'app/queries/media/createFetchMediaAnalysisStatusQueryFn';
import { mediaKeys } from 'app/queries/media/mediaKeys';
import { useEffect, useMemo } from 'react';
import { collectPendingMediaAnalyses, type TPendingMediaAnalysis } from '../helpers/collectPendingMediaAnalyses';
import { resolveMediaAnalysisOutcome } from '../helpers/resolveMediaAnalysisOutcome';

const MEDIA_ANALYSIS_POLL_INTERVAL_MS = 3000;

type TParams = {
  files: TFileWithMeta[];
  setExtra: (id: string, extra: Record<string, unknown>) => void;
};

export const useMediaAnalysisPolling = ({ files, setExtra }: TParams) => {
  const { call } = useAPI();
  const queryClient = useQueryClient();

  const pendingAnalysesSignature = JSON.stringify(collectPendingMediaAnalyses(files));
  const pendingAnalyses = useMemo(
    () => JSON.parse(pendingAnalysesSignature) as TPendingMediaAnalysis[],
    [pendingAnalysesSignature],
  );

  const analysisQueries = useQueries({
    queries: pendingAnalyses.map(({ mediaId }) => ({
      queryKey: mediaKeys.analysisStatus(mediaId),
      queryFn: createFetchMediaAnalysisStatusQueryFn(call),
      refetchInterval: (query: {
        state: { data?: CommonBullJobResponseDto; dataUpdateCount: number; errorUpdateCount: number };
      }) =>
        resolveMediaAnalysisOutcome(query.state.data, query.state.dataUpdateCount + query.state.errorUpdateCount)
          .status === 'pending'
          ? MEDIA_ANALYSIS_POLL_INTERVAL_MS
          : false,
      refetchIntervalInBackground: true,
      gcTime: 0,
    })),
  });
  const analysisUpdates = analysisQueries.map((query) => `${query.dataUpdatedAt}:${query.errorUpdatedAt}`).join(',');

  useEffect(() => {
    if (!analysisUpdates) {
      return;
    }
    pendingAnalyses.forEach(({ fileId, mediaId }) => {
      const state = queryClient.getQueryState<CommonBullJobResponseDto>(mediaKeys.analysisStatus(mediaId));
      const outcome = resolveMediaAnalysisOutcome(
        state?.data,
        (state?.dataUpdateCount ?? 0) + (state?.errorUpdateCount ?? 0),
      );
      if (outcome.status === 'done') {
        setExtra(fileId, { duration: outcome.duration, analysisStatus: 'done' });
      } else if (outcome.status === 'error') {
        setExtra(fileId, { analysisStatus: 'error' });
      }
    });
  }, [analysisUpdates, pendingAnalyses, queryClient, setExtra]);
};
